import { TRADEMARK_SERVICE_KEYS } from '_constants/trademarkServices';
import { PROCESSING_OPTIONS_KEYS } from '_constants/processing';
import { PRODUCT_CODES } from '_constants/products';
import { STORAGE_KEYS } from '_constants/storage';
import { ROUTES } from '_constants/routes';
import { FIELDS_KEYS } from '_constants/forms';
import { restoreLocalValue, restoreSessionValue } from '_helpers/storage';

const createAddress = (form, prefix) => {
  if (prefix) {
    return {
      address1: form[prefix + 'Address'],
      city: form[prefix + 'City'],
      state: form[prefix + 'State'],
      zip: form[prefix + 'Zip'],
      country: 'US',
    };
  }

  return {
    address1: form.address,
    city: form.city,
    state: form.state,
    zip: form.zip,
    country: 'US',
  };
};

const createCustomer = (form) => {
  const customer = {
    email: form.email,
    phone: form.phone ? form.phone.replace(/\D/g, '') : null,
    firstName: form.firstName,
    lastName: form.lastName,
  };

  if (form.address) {
    customer.billingAddress = createAddress(form);
  }

  const requestUri = restoreLocalValue(STORAGE_KEYS.requestUri);

  if (requestUri) {
    customer.firstCLickURL = requestUri;
  }

  return customer;
};

const createDate = (form, dateId) => {
  if (!form[dateId + 'Year'] && form[dateId + 'Year'].length === 0) {
    return null;
  }

  const year = parseInt(form[dateId + 'Year']);
  const month = parseInt(form[dateId + 'Month']) - 1; // to start from 0
  const day = parseInt(form[dateId + 'Day']);

  if (!Number.isNaN(new Date(year, month, day, 10, 0, 0).getTime())) {
    return new Date(year, month, day, 10, 0, 0).toISOString().split('T')[0];
  }

  return null;
};

const parseDate = (isoString) => {
  const milliseconds = Date.parse(isoString);
  const dateObj = new Date(milliseconds);
  return {
    date: `${dateObj.getDate()}`,
    month: `${dateObj.getMonth() + 1}`,
    year: `${dateObj.getFullYear()}`,
  };
};

const nameInMark = (product, form) => {
  if (form.isIncludeLivingPersonName) {
    if (!form.isIncludeNicknameName) {
      if (!form.isOtherLivingName) {
        product.includedName = 'OTHER_NAME';
      } else {
        product.includedName = 'OTHER_LIVING_NAME';
        product.includedNameValue = form.otherLivingName;
      }
    } else {
      product.includedName = 'OWNER_NAME';
      product.includedNameValue = form.ownName;
    }
  } else {
    product.includedName = 'NONE';
  }
};

const translationWords = (product, form) => {
  product.includeTranslationWords = form.includeTranslationWords;

  if (form.includeTranslationWords) {
    product.translation = form.translation;
  }
};

const currentTrademark = (product, form) => {
  product.currentTrademark = form.currentTrademark;

  if (form.currentTrademark) {
    if (form.firstUseInCommerceYear) {
      product.firstUseInCommerce = createDate(form, 'firstUseInCommerce');
    }
    if (form.firstUseAnywhereYear) {
      product.firstUseAnywhere = createDate(form, 'firstUseAnywhere');
    }
  }
};

const createTrademarkRegistrationBody = (form = {}, packageInfo) => {
  if (form.step === 0) {
    return null;
  }

  const products = [];

  packageInfo?.products.forEach((productCode) => {
    const product = {};

    if (productCode === PRODUCT_CODES.tmRegisterTrademark) {
      product.code = productCode;
      product.trademarkType = form.trademarkType;
      product.markDesc = form.markDesc;

      if (form.trademarkType === 'LOGO') {
        product.literalElements = form.literalElements;
        product.logoColoredVariety = form.logoColoredVariety;

        if (!form.logoColoredVariety) {
          product.colorsInLogo = form.colorsInLogo;
        }
      }

      nameInMark(product, form);
      translationWords(product, form);
      currentTrademark(product, form);

      product.individual = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone ? form.phone.replace(/\D/g, '') : null,
        countryOfCitizenship: form.countryOfCitizenship,
        address: createAddress(form),
      };

      if (form.individualOrgOrInd === 'Organization') {
        product.organization = {
          usBased: form.isNonUsBased === 'USBased' ? true : false,
          name: form.orgName,
          type: form.orgType,
          stateOrCountryOfFormation:
            form.isNonUsBased === 'USBased'
              ? form.orgState
              : form.formationCountry,
          position: form.orgPosition,
        };
      }

      if (form.individualOrgOrInd === 'Individual') {
        product.coOwners = form.coOwners.map(
          ({ address, city, state, zip, ...restCoOwner }) => {
            return {
              ...restCoOwner,
              address: createAddress({ address, city, state, zip }),
            };
          }
        );
      }

      if (form.classes.length && form.classes[0]?.goodsCategory) {
        product.classes = form.classes;
      }
    }

    if (
      [
        PRODUCT_CODES.tmSearchBasic,
        PRODUCT_CODES.tmSearchFederalState,
      ].includes(productCode)
    ) {
      product.code = productCode;
      product.searchTermType = form.trademarkType;
      product.text = form.markDesc;
    }

    if (productCode === PRODUCT_CODES.tmMonitoring) {
      product.code = productCode;
      product.trademarkName = form.markDesc;
    }

    if (productCode === PRODUCT_CODES.tmOfficeActionResponse) {
      product.code = productCode;
      product.trademarkName = form.markDesc;
      product.trademarkOwner = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone ? form.phone.replace(/\D/g, '') : null,
      };
    }

    if (productCode === PRODUCT_CODES.tmRushProcessing) {
      product.code = productCode;
    }

    if (
      productCode === PRODUCT_CODES.tmRegisterTrademark ||
      form.processingOption
    ) {
      products.push(product);
    }
  });

  return {
    customer: createCustomer(form),
    order: {
      processingOption:
        form.processingOption || PROCESSING_OPTIONS_KEYS.standard,
      campaignId: form.campaignId,
    },
    products,
  };
};

const createComprehensiveSearchBody = (form = {}, packageInfo) => {
  const products = [];

  packageInfo?.products.forEach((productCode) => {
    const product = {};

    if (
      [
        PRODUCT_CODES.tmSearchFederalState,
        PRODUCT_CODES.tmSearchCommonLow,
        PRODUCT_CODES.tmSearchGlobal,
      ].includes(productCode)
    ) {
      product.code = productCode;
      product.searchTermType = form.trademarkType;
      product.text = form.markDesc;

      if (form.trademarkType === 'LOGO') {
        product.logoWords = form.literalElements;
      }
    }

    if (productCode === PRODUCT_CODES.tmRushProcessing) {
      product.code = productCode;
    }

    products.push(product);
  });

  return {
    customer: createCustomer(form),
    order: {
      processingOption:
        form.processingOption || PROCESSING_OPTIONS_KEYS.standard,
    },
    products,
  };
};

const createStatementOfUseBody = (form = {}, packageInfo) => {
  const products = [];

  packageInfo?.products.forEach((productCode) => {
    const product = {};

    if (productCode === PRODUCT_CODES.tmStatementOfUse) {
      product.code = productCode;

      if (form.isSerialNumber) {
        product.serialNumber = form.serialNumber;
      }

      if (!form.isSerialNumber) {
        product.trademarkName = form.markDesc;
        product.trademarkOwner = {
          firstName: form.markOwnerFirstName,
          lastName: form.markOwnerLastName,
          email: form.markOwnerEmail,
          phone: form.markOwnerPhone
            ? form.markOwnerPhone.replace(/\D/g, '')
            : null,
        };
      }

      if (form.isNewAddress) {
        product.newAddress = createAddress(form, 'new');
      }

      if (form.isNewMarkOwner) {
        product.changesToOwner = {
          firstName: form.newMarkOwnerFirstName,
          lastName: form.newMarkOwnerLastName,
        };
      }

      if (form.isNoticeOfAllowanceCovered) {
        product.coversAllGoods = true;
        product.specimenClass = form.classType;
      }

      if (!form.isNoticeOfAllowanceCovered) {
        product.coversAllGoods = false;
        product.classesOrGoodsNotIncludedInAllegation =
          form.classesOrGoodsNotCovered;

        if (form.isExtensionOnClasses) {
          product.itemsCoveredByThisStatement = form.itemsCovered;
          product.itemsCoveredByThisExtensionRequest = form.itemsExtension;
        }
      }

      if (form.firstUseInCommerceYear) {
        product.firstUseInCommerce = createDate(form, 'firstUseInCommerce');
      }

      if (form.firstUseAnywhereYear) {
        product.firstUseAnywhere = createDate(form, 'firstUseAnywhere');
      }

      if (!form.isSpecimenFile) {
        product.websiteURL = form.specimenUrl;
      }
    }

    if (productCode === PRODUCT_CODES.tmRushProcessing) {
      product.code = productCode;
    }

    products.push(product);
  });

  return {
    customer: createCustomer(form),
    order: {
      processingOption:
        form.processingOption || PROCESSING_OPTIONS_KEYS.standard,
    },
    products,
  };
};

const createTrademarkMonitoringBody = (form = {}, packageInfo) => {
  const products = [];

  packageInfo?.products.forEach((productCode) => {
    const product = {};

    if (productCode === PRODUCT_CODES.tmMonitoring) {
      product.code = productCode;
      product.trademarkName = form.trademarkName;

      product.owner = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone ? form.phone.replace(/\D/g, '') : null,
      };
    }

    if (productCode === PRODUCT_CODES.tmRushProcessing) {
      product.code = productCode;
    }

    products.push(product);
  });

  return {
    customer: createCustomer(form),
    order: {
      processingOption:
        form.processingOption || PROCESSING_OPTIONS_KEYS.standard,
    },
    products,
  };
};

const createOfficeActionResponseBody = (form = {}, packageInfo) => {
  const products = [];

  packageInfo?.products.forEach((productCode) => {
    const product = {};

    if (productCode === PRODUCT_CODES.tmOfficeActionResponse) {
      product.code = productCode;

      if (form.isSerialNumber) {
        product.serialNumber = form.serialNumber;
      }

      if (!form.isSerialNumber) {
        product.trademarkName = form.markDesc;
        product.trademarkOwner = {
          firstName: form.markOwnerFirstName,
          lastName: form.markOwnerLastName,
          email: form.markOwnerEmail,
          phone: form.markOwnerPhone
            ? form.markOwnerPhone.replace(/\D/g, '')
            : null,
        };
      }

      if (form.isNewAddress) {
        product.newAddress = createAddress(form, 'new');
      }

      if (form.isNewMarkOwner) {
        product.changesToOwner = {
          firstName: form.newMarkOwnerFirstName,
          lastName: form.newMarkOwnerLastName,
        };
      }

      if (form.isSpecimenNeeded === 'YES') {
        product.newSpecimenWasUploaded = true;
        product.specimenClass = form.classType;
      }
    }

    if (productCode === PRODUCT_CODES.tmRushProcessing) {
      product.code = productCode;
    }

    products.push(product);
  });

  return {
    customer: createCustomer(form),
    order: {
      processingOption:
        form.processingOption || PROCESSING_OPTIONS_KEYS.standard,
    },
    products,
  };
};

const createRenewalBody = (form = {}, packageInfo) => {
  const products = [];

  packageInfo?.products.forEach((productCode) => {
    const product = {};

    if (productCode === PRODUCT_CODES.tmRenewal) {
      product.code = productCode;

      if (form.isSerialNumber) {
        product.serialNumber = form.serialNumber;
      }

      if (!form.isSerialNumber) {
        product.trademarkName = form.markDesc;
        product.trademarkOwner = {
          firstName: form.markOwnerFirstName,
          lastName: form.markOwnerLastName,
          email: form.markOwnerEmail,
          phone: form.markOwnerPhone
            ? form.markOwnerPhone.replace(/\D/g, '')
            : null,
        };
      }

      product.inUseStatus = form.isClassesInUse;

      if (form.isClassesInUse === 'YES_ON_SOME') {
        product.goodsAndServicesStillInUse = form.classesStillInUse;
        product.goodsAndServicesNoLongerInUse = form.classesNoLongerInUse;
      }

      if (!form.isAddressSame) {
        product.newAddress = createAddress(form, 'new');
      }

      if (!form.isMarkOwnerSame) {
        product.newFirstName = form.newMarkOwnerFirstName;
        product.newLastName = form.newMarkOwnerLastName;
      }

      if (!form.isSpecimenFile) {
        product.websiteURL = form.specimenUrl;
      }
    }

    if (productCode === PRODUCT_CODES.tmRushProcessing) {
      product.code = productCode;
    }

    products.push(product);
  });

  return {
    customer: createCustomer(form),
    order: {
      processingOption:
        form.processingOption || PROCESSING_OPTIONS_KEYS.standard,
    },
    products,
  };
};

const createExtensionBody = (form = {}, packageInfo) => {
  const products = [];

  packageInfo?.products.forEach((productCode) => {
    const product = {};

    if (productCode === PRODUCT_CODES.tmExtension) {
      product.code = productCode;

      if (form.isSerialNumber) {
        product.serialNumber = form.serialNumber;
      }

      if (!form.isSerialNumber) {
        product.trademarkName = form.markDesc;
        product.trademarkOwner = {
          firstName: form.markOwnerFirstName,
          lastName: form.markOwnerLastName,
          email: form.markOwnerEmail,
          phone: form.markOwnerPhone
            ? form.markOwnerPhone.replace(/\D/g, '')
            : null,
        };
      }

      if (form.isNewAddress) {
        product.newAddress = createAddress(form, 'new');
      }

      if (form.isNewMarkOwner) {
        product.changesToOwner = {
          firstName: form.newMarkOwnerFirstName,
          lastName: form.newMarkOwnerLastName,
        };
      }

      if (!form.isNoticeOfAllowanceCovered) {
        product.missedGoodsOrServices = form.classesOrGoodsNotCovered;
      }

      if (form.isExtensionOnClasses) {
        product.itemsCoveredByThisStatement = form.itemsCovered;
        product.itemsCoveredByThisExtensionRequest = form.itemsExtension;
      }

      if (form.isExtendedInThePast) {
        product.timesDeadlineWasExtended = form.timesDeadlineWasExtended;
        product.extensionReason = form.extensionReason;

        if (form.extensionReason === 'Other') {
          product.extensionReasonOther = form.extensionReasonOther;
        }
      }
    }

    if (productCode === PRODUCT_CODES.tmRushProcessing) {
      product.code = productCode;
    }

    products.push(product);
  });

  return {
    customer: createCustomer(form),
    order: {
      processingOption:
        form.processingOption || PROCESSING_OPTIONS_KEYS.standard,
    },
    products,
  };
};

const createRevivalBody = (form = {}, packageInfo) => {
  const products = [];

  packageInfo?.products.forEach((productCode) => {
    const product = {};

    if (productCode === PRODUCT_CODES.tmRevivalFromAbandonment) {
      product.code = productCode;

      if (form.isSerialNumber) {
        product.serialNumber = form.serialNumber;
      }

      if (!form.isSerialNumber) {
        product.trademarkName = form.markDesc;
        product.trademarkOwner = {
          firstName: form.markOwnerFirstName,
          lastName: form.markOwnerLastName,
          email: form.markOwnerEmail,
          phone: form.markOwnerPhone
            ? form.markOwnerPhone.replace(/\D/g, '')
            : null,
        };
      }

      if (form.service === TRADEMARK_SERVICE_KEYS.officeActionResponse) {
        product.officeActionStatus = form.officeActionStatus;
        product.statementOfUseStatus = 'NONE';
      }

      if (form.service === TRADEMARK_SERVICE_KEYS.statementOfUse) {
        product.officeActionStatus = 'NONE';
        product.statementOfUseStatus = form.statementOfUseStatus;
      }
    }

    if (productCode === PRODUCT_CODES.tmRushProcessing) {
      product.code = productCode;
    }

    products.push(product);
  });

  return {
    customer: createCustomer(form),
    order: {
      processingOption:
        form.processingOption || PROCESSING_OPTIONS_KEYS.standard,
    },
    products,
  };
};

const createLogoDesignBody = (form = {}, packageInfo) => {
  const products = [];

  packageInfo?.products.forEach((productCode) => {
    const product = {};

    if (productCode === PRODUCT_CODES.tmLogoDesignService) {
      product.code = productCode;
      product.logoName = form.logoName;
      product.slogan = form.slogan || null;
      product.businessIndustry = form.businessIndustry;
      product.businessDescription = form.businessDescription;
      product.businessProducts = form.businessProducts;
      product.targetAudience = form.targetAudience;
      product.businessWebsite = form.businessWebsite || null;
      product.lettersType = form.lettersType;
      product.customersConception = form.customersConception;
      product.logoCategory = form.logoCategory;
      product.logoUse = form.logoUse;
      product.logoUseDescription = form.logoUseDescription || null;
    }

    if (productCode === PRODUCT_CODES.tmRushProcessing) {
      product.code = productCode;
    }

    products.push(product);
  });

  return {
    customer: createCustomer(form),
    order: {
      processingOption:
        form.processingOption || PROCESSING_OPTIONS_KEYS.standard,
    },
    products,
  };
};

export const createOrderBody = (trademarkServiceKey, form, packageInfo) => {
  const MAP = {
    [TRADEMARK_SERVICE_KEYS.trademarkRegistration]:
      createTrademarkRegistrationBody,
    [TRADEMARK_SERVICE_KEYS.comprehensiveTrademarkSearch]:
      createComprehensiveSearchBody,
    [TRADEMARK_SERVICE_KEYS.statementOfUse]: createStatementOfUseBody,
    [TRADEMARK_SERVICE_KEYS.trademarkMonitoring]: createTrademarkMonitoringBody,
    [TRADEMARK_SERVICE_KEYS.officeActionResponse]:
      createOfficeActionResponseBody,
    [TRADEMARK_SERVICE_KEYS.trademarkRenewal]: createRenewalBody,
    [TRADEMARK_SERVICE_KEYS.requestExtension]: createExtensionBody,
    [TRADEMARK_SERVICE_KEYS.trademarkRevive]: createRevivalBody,
    [TRADEMARK_SERVICE_KEYS.logoDesignService]: createLogoDesignBody,
  };

  const creator = MAP[trademarkServiceKey];

  return creator ? creator(form, packageInfo) : undefined;
};

export const fillOrderBodyWithSourceData = (orderBody, device, salePageURL) => {
  if (device) {
    orderBody.order.device = device;
  }

  if (salePageURL) {
    orderBody.order.salePageURL = salePageURL;
  }

  if (restoreLocalValue(STORAGE_KEYS.gclid)) {
    orderBody.order.clid = restoreLocalValue(STORAGE_KEYS.gclid);
  }

  if (restoreLocalValue(STORAGE_KEYS.source)) {
    orderBody.order.source = restoreLocalValue(STORAGE_KEYS.source);
  }

  if (restoreLocalValue(STORAGE_KEYS.keyword)) {
    orderBody.order.keyword = restoreLocalValue(STORAGE_KEYS.keyword);
  }

  if (restoreLocalValue(STORAGE_KEYS.adAccount)) {
    orderBody.order.adAccount = restoreLocalValue(STORAGE_KEYS.adAccount);
  }

  const firstClickURL = restoreLocalValue(STORAGE_KEYS.firstClickURL);
  const saleURL =
    restoreSessionValue(STORAGE_KEYS.saleURL) || firstClickURL || null;

  if (saleURL) {
    orderBody.order.saleURL = saleURL;
  }

  if (firstClickURL) {
    orderBody.order.firstClickURL = firstClickURL;
  }

  const fingerprint = restoreLocalValue(STORAGE_KEYS.fingerprint);
  if (fingerprint) {
    orderBody.order.fingerprint = fingerprint;
  }
};

const parseTrademarkRegistrationOrder = (data, cid) => {
  const { products } = data || {};
  const mainProduct =
    products?.find(
      (product) => product.code?.code === PRODUCT_CODES.tmRegisterTrademark
    ) || {};

  const form = {
    step: 2,
    maxStep: 2,
  };

  form.trademarkType = mainProduct.trademarkType;
  form.markDesc = mainProduct.markDesc;

  if (mainProduct.trademarkType === 'LOGO') {
    form.literalElements = mainProduct.literalElements;
    form.logoColoredVariety = mainProduct.logoColoredVariety;

    if (mainProduct.colorsInLogo) {
      form.colorsInLogo = mainProduct.colorsInLogo;
      form.logoColoredVariety = false;
    }
  }

  if (mainProduct.includedName === 'NONE') {
    form.isIncludeLivingPersonName = false;
  } else {
    form.isIncludeLivingPersonName = true;

    if (mainProduct.includedName === 'OWNER_NAME') {
      form.isIncludeNicknameName = true;
      form.ownName = mainProduct.includedNameValue;
    } else {
      form.isIncludeNicknameName = false;

      if (mainProduct.includedName === 'OTHER_LIVING_NAME') {
        form.isOtherLivingName = true;
        form.otherLivingName = mainProduct.includedNameValue;
      } else {
        form.isOtherLivingName = false;
      }
    }
  }

  if (mainProduct.includeTranslationWords) {
    form.includeTranslationWords = mainProduct.includeTranslationWords;
  }

  if (mainProduct.translation) {
    form.includeTranslationWords = true;
    form.translation = mainProduct.translation;
  }

  if (mainProduct.currentTrademark) {
    form.currentTrademark = true;

    if (mainProduct.firstUseInCommerce) {
      const {
        date: firstUseInCommerceDay,
        month: firstUseInCommerceMonth,
        year: firstUseInCommerceYear,
      } = parseDate(mainProduct.firstUseInCommerce);

      form.firstUseInCommerceYear = firstUseInCommerceYear;
      form.firstUseInCommerceMonth = firstUseInCommerceMonth;
      form.firstUseInCommerceDay = firstUseInCommerceDay;
    }

    if (mainProduct.firstUseAnywhere) {
      const {
        date: firstUseAnywhereDay,
        month: firstUseAnywhereMonth,
        year: firstUseAnywhereYear,
      } = parseDate(mainProduct.firstUseAnywhere);

      form.firstUseAnywhereYear = firstUseAnywhereYear;
      form.firstUseAnywhereMonth = firstUseAnywhereMonth;
      form.firstUseAnywhereDay = firstUseAnywhereDay;
    }
  } else {
    form.currentTrademark = false;
  }

  form.individualOrgOrInd = 'Individual';

  if (mainProduct.individual) {
    form.firstName = mainProduct.individual.firstName;
    form.lastName = mainProduct.individual.lastName;
    form.email = mainProduct.individual.email;
    form.phone = mainProduct.individual.phone;
    form.address = mainProduct.individual.address.address1;
    form.city = mainProduct.individual.address.city;
    form.state = mainProduct.individual.address.state;
    form.zip = mainProduct.individual.address.zip;
    form.countryOfCitizenship = mainProduct.individual.countryOfCitizenship;
    form.coOwners = mainProduct.coOwners?.map(({ address, ...restCoOwner }) => {
      return {
        ...restCoOwner,
        address: address?.address1 || '',
        city: address?.city || '',
        state: address?.state || '',
        zip: address?.zip || '',
      };
    });
  }

  if (mainProduct.organization) {
    form.individualOrgOrInd = 'Organization';
    form.orgName = mainProduct.organization.name;
    form.orgType = mainProduct.organization.type;
    form.orgPosition = mainProduct.organization.position;

    if (mainProduct.organization.usBased) {
      form.isNonUsBased = 'USBased';
      form.orgState = mainProduct.organization.stateOrCountryOfFormation;
    } else {
      form.isNonUsBased = 'NonUSBased';
      form.formationCountry =
        mainProduct.organization.stateOrCountryOfFormation;
    }
  }

  if (mainProduct.classes.length) {
    form.step = 3;
    form.maxStep = 3;
    form.classes = mainProduct.classes;
  }

  if (
    mainProduct.processingOption &&
    mainProduct.processingOption !== PROCESSING_OPTIONS_KEYS.standard
  ) {
    form.step = 4;
    form.maxStep = 4;
    form.processingOption = mainProduct.processingOption;
  }

  if (cid) {
    form.campaignId = cid === 't' ? 13 : 12;
  }

  return {
    form,
    products: products?.map(({ code }) => code.code),
    paymentStatus: mainProduct.paymentStatus,
  };
};

export const parseOrder = (trademarkServiceKey, data, cid) => {
  const MAP = {
    [TRADEMARK_SERVICE_KEYS.trademarkRegistration]:
      parseTrademarkRegistrationOrder,
  };

  const parser = MAP[trademarkServiceKey];

  return parser ? parser(data, cid) : undefined;
};

export const createOrderBeforePayment = (data, token) => {
  const { order, products } = data || {};
  const {
    id,
    crmOrderId,
    crmOrderStatus,
    orderStatus,
    discountCouponType,
    updated,
  } = order || {};

  return {
    id,
    crmOrderId,
    crmOrderStatus,
    orderStatus,
    discountCouponType,
    tokenWithDiscount: token,
    products: products?.map(
      ({ id, code: { code }, paymentStatus, crmPaymentStatus }) => {
        return {
          id,
          code,
          paymentStatus,
          crmPaymentStatus,
        };
      }
    ),
    token: { token, orderUids: [id], expires: null },
    updated,
  };
};

export const getRouteByServiceKey = (trademarkServiceKey) => {
  const MAP = {
    [TRADEMARK_SERVICE_KEYS.trademarkRegistration]:
      ROUTES.trademarkRegistrationFiling,
    [TRADEMARK_SERVICE_KEYS.comprehensiveTrademarkSearch]:
      ROUTES.comprehensiveSearchFiling,
    [TRADEMARK_SERVICE_KEYS.statementOfUse]: ROUTES.statementOfUseFiling,
    [TRADEMARK_SERVICE_KEYS.trademarkMonitoring]:
      ROUTES.trademarkMonitoringFiling,
    [TRADEMARK_SERVICE_KEYS.officeActionResponse]:
      ROUTES.officeActionResponseFiling,
    [TRADEMARK_SERVICE_KEYS.trademarkRenewal]: ROUTES.renewalFiling,
    [TRADEMARK_SERVICE_KEYS.requestExtension]: ROUTES.requestForExtensionFiling,
    [TRADEMARK_SERVICE_KEYS.trademarkRevive]:
      ROUTES.revivalFromAbandonmentFiling,
    [TRADEMARK_SERVICE_KEYS.logoDesignService]: ROUTES.logoDesignServiceFiling,
  };

  const route = MAP[trademarkServiceKey];

  return route ? route : ROUTES.home;
};
